import React, { useEffect, useMemo, useState } from 'react';

import swal from 'sweetalert';

import CasinoCRMApi from '../../../../../casino/Api/CRM/CasinoCRMApi';
import { formatCurrency } from '../../../../../utils/formatCurrency';
import Search from '../../../../elements/Search';
import Table from '../../../../elements/Table';
import Card from '../../../../elements/common/Card';
import FilterByColumn, { Filter } from '../../../../elements/common/FilterByColumn';
import SortButton from '../../../../elements/common/SortButton';
import TableLoader from '../../../../elements/common/TableLoader';
import Paginator from '../../../../elements/paginator';
import { roundNumber } from '../../../../helpers/formUtils';
import useAutoPaginator from '../../../../hooks/useAutoPaginator';
import GroupBySelect, { GroupBy } from '../../../pages/Dashboard/Elements/GroupBySelect';

interface Props {
    userId: number;
}

export default function CasinoDetailedBreakdown({ userId }: Props) {
    const [groupBy, setGroupBy] = useState<GroupBy>('name');
    const [isGroupByGameName, setIsGroupByGameName] = useState<boolean>(true);
    const [totals, setTotals] = useState<{ ggr: number; bets: number; wagered: number; won: number } | null>(null);

    const fetchPaginatedData = async (page, params) => {
        try {
            return await CasinoCRMApi.detailedBreakdown.getPaginatedByUserId(page, userId, {
                params: { ...params, groupBy },
            });
        } catch (error) {
            await swal({
                title: 'Error',
                text: 'There was an error fetching the paginated data.',
                icon: 'error',
            });
            throw error;
        }
    };

    const fetchTotalsData = async (params) => {
        try {
            return await CasinoCRMApi.detailedBreakdown.getTotalsByUserId(userId, {
                params: { ...params, groupBy },
            });
        } catch (error) {
            await swal({
                title: 'Error',
                text: 'There was an error fetching the totals data.',
                icon: 'error',
            });
            throw error;
        }
    };

    const { items, updateItems, isLoading, auto } = useAutoPaginator(async (page, params) => {
        const paginatedData = await fetchPaginatedData(page, params);
        const totalsData = await fetchTotalsData(params);

        setTotals(totalsData);

        return paginatedData;
    });

    useEffect(() => {
        updateItems(1);
    }, [groupBy]);

    const updateOrderByOnGroupByChange = (newGroupBy: GroupBy) => {
        setGroupBy(newGroupBy);
        setIsGroupByGameName(newGroupBy === 'name');
    };

    const columns = useMemo(
        () => [
            {
                Header: isGroupByGameName ? (
                    <SortButton column="casino_games.name" label="Game" {...auto.sort} />
                ) : (
                    <SortButton column="casino_games.creator_name" label="Provider" {...auto.sort} />
                ),
                accessor: isGroupByGameName ? 'name' : 'creator_name',
            },
            {
                Header: <SortButton column="bets" label="Bets" {...auto.sort} />,
                accessor: 'bets',
            },
            {
                Header: <SortButton column="wagered" label="Wagered" {...auto.sort} />,
                accessor: 'wagered',
                Cell: ({ value }) => formatCurrency(value, 2),
            },
            {
                Header: <SortButton column="won" label="Won" {...auto.sort} />,
                accessor: 'won',
                Cell: ({ value }) => formatCurrency(value, 2),
            },
            {
                Header: (
                    <SortButton
                        column="rtp"
                        label="RTP"
                        definition="Return to player. Formula: amount of wins/amount of bets"
                        {...auto.sort}
                    />
                ),
                accessor: 'rtp',
                Cell: ({ value }) => `${roundNumber(value * 100, 2)}%`,
            },
            {
                Header: <SortButton column="ggr" label="GGR" definition="Gross game revenue" {...auto.sort} />,
                accessor: 'ggr',
                Cell: ({ value }) => formatCurrency(value, 2),
            },
            {
                Header: (
                    <SortButton
                        column="ggr_margin"
                        label="GGR margin"
                        definition="Formula: GGR/wagered"
                        {...auto.sort}
                    />
                ),
                accessor: 'ggr_margin',
                Cell: ({ value }) => roundNumber(value * 100, 2),
            },
        ],
        [isGroupByGameName]
    );

    const combinedData = useMemo(() => {
        if (!totals) return items;
        return [
            ...items,
            {
                ...totals,
                name: 'Total',
                creator_name: 'Total',
                bets: totals.bets ?? 0,
                wagered: totals.wagered ?? 0,
                won: totals.won ?? 0,
                rtp: totals.wagered > 0 ? totals.won / totals.wagered : 0,
                ggr: totals.ggr ?? 0,
                ggr_margin: totals.wagered > 0 ? totals.ggr / totals.wagered : 0,
            },
        ];
    }, [items, totals]);

    return (
        <Card title="Casino Detailed Breakdown">
            <div className="d-flex">
                <Search {...auto.search} />
                <GroupBySelect groupBy={groupBy} setGroupBy={updateOrderByOnGroupByChange} />
                <FilterByColumn {...auto.filter}>
                    <Filter column="casino_transactions.created_at" label="Created at" type="dateRange" />
                </FilterByColumn>
            </div>
            <TableLoader isLoading={isLoading}>
                <Table columns={columns} data={combinedData} additionalClasses="model-list casino-detailed-breakdown" />
            </TableLoader>
            <Paginator {...auto.paginator} />
        </Card>
    );
}
